import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

/**
 * Platform-agnostic storage interface.
 * Web uses localStorage, mobile uses AsyncStorage/SecureStore.
 */
export interface StorageAdapter {
  getItem(key: string): Promise<string | null>
  setItem(key: string, value: string): Promise<void>
  removeItem(key: string): Promise<void>
}

/**
 * Platform-agnostic navigation adapter.
 * Web uses react-router, mobile uses react-navigation.
 */
export interface NavigationAdapter {
  navigate(route: string): void
}

let _storage: StorageAdapter | null = null
let _navigation: NavigationAdapter | null = null
let _apiClient: AxiosInstance | null = null

/**
 * Initialize the API client with platform-specific adapters.
 * Must be called once at app startup (both web and mobile).
 */
export function initApiClient(config: {
  baseURL: string
  storage: StorageAdapter
  navigation: NavigationAdapter
}): AxiosInstance {
  _storage = config.storage
  _navigation = config.navigation

  _apiClient = axios.create({
    baseURL: config.baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000,
  })

  // Request interceptor to add auth token
  _apiClient.interceptors.request.use(async (axiosConfig: InternalAxiosRequestConfig) => {
    const token = await _storage!.getItem('token')
    if (token) {
      axiosConfig.headers.Authorization = `Bearer ${token}`
    }
    return axiosConfig
  })

  // Response interceptor for token refresh
  _apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true

        try {
          const refreshToken = await _storage!.getItem('refreshToken')
          if (refreshToken) {
            const response = await axios.post(`${config.baseURL}/auth/refresh`, {
              refreshToken,
            })

            const { token } = response.data
            await _storage!.setItem('token', token)
            originalRequest.headers.Authorization = `Bearer ${token}`

            return _apiClient!(originalRequest)
          }
        } catch (refreshError) {
          await _storage!.removeItem('token')
          await _storage!.removeItem('refreshToken')
          _navigation!.navigate('/login')
        }
      }

      return Promise.reject(error)
    }
  )

  return _apiClient
}

/**
 * Get the initialized API client instance.
 * Throws if initApiClient hasn't been called.
 */
export function getApiClient(): AxiosInstance {
  if (!_apiClient) {
    throw new Error(
      'API client not initialized. Call initApiClient() at app startup.'
    )
  }
  return _apiClient
}

/**
 * Get the storage adapter.
 */
export function getStorage(): StorageAdapter {
  if (!_storage) {
    throw new Error(
      'Storage not initialized. Call initApiClient() at app startup.'
    )
  }
  return _storage
}